import { useEffect, useMemo, useRef, useState } from 'react';
import QRCodeGenerator from 'qrcode';
import {
  BookOpen,
  Check,
  ChevronDown,
  Download,
  Edit3,
  ExternalLink,
  Eye,
  Layers,
  ListChecks,
  Loader2,
  Plus,
  Power,
  PowerOff,
  RefreshCcw,
  Search,
  Sparkles,
  Star,
  Trash2,
  UtensilsCrossed,
} from 'lucide-react';
import { Link } from 'react-router-dom';
import { menuTemplatesApi, menusApi } from '../../api';
import { useAlerts } from '../../components/feedback/AlertProvider';
import { useConfirm } from '../../components/feedback/ConfirmProvider';
import { useAuth } from '../../contexts/AuthContext';
import { useLanguage } from '../../contexts/LanguageContext';
import resolveMediaUrl from '../../utils/resolveMediaUrl';
import translateApiError from '../../utils/translateApiError';
import '../../styles/Menus/menus.css';
const DEFAULT_ESTABLISHMENT_ID = 1;
const SELECTED_MENU_STORAGE_KEY = 'selected_menu_id';
const SELECTED_MENU_ESTABLISHMENT_STORAGE_KEY =
  'selected_menu_establishment_id';
const toArray = (value) => {
  if (Array.isArray(value)) return value;
  if (Array.isArray(value?.data)) return value.data;
  if (value && typeof value === 'object') {
    return Object.values(value);
  }
  return [];
};
const translateFallback = (
  t,
  key,
  fallback,
) => {
  const value = t(key);
  return value === key ? fallback : value;
};
const getBooleanStatus = (value) => {
  return (
    value === true ||
    value === 1 ||
    value === '1' ||
    value === 'true'
  );
};
const getCount = (...values) => {
  const found = values.find(
    (value) =>
      value !== undefined &&
      value !== null &&
      value !== '',
  );
  return Number(found || 0);
};
const formatNumber = (value, language) => {
  const number = Number(value || 0);
  try {
    return new Intl.NumberFormat(language).format(number);
  } catch {
    return String(number);
  }
};
const getMenuImageUrl = (menu) => {
  return resolveMediaUrl(
    menu?.image_url ||
      menu?.image_path ||
      menu?.image ||
      '',
  );
};
const saveSelectedMenu = (
  menuId,
  establishmentId,
) => {
  if (!menuId) {
    localStorage.removeItem(
      SELECTED_MENU_STORAGE_KEY,
    );
    localStorage.removeItem(
      SELECTED_MENU_ESTABLISHMENT_STORAGE_KEY,
    );
    return;
  }
  localStorage.setItem(
    SELECTED_MENU_STORAGE_KEY,
    String(menuId),
  );
  if (establishmentId) {
    localStorage.setItem(
      SELECTED_MENU_ESTABLISHMENT_STORAGE_KEY,
      String(establishmentId),
    );
  }
};
function CustomSelect({
  value,
  options = [],
  onChange,
  disabled = false,
  ariaLabel,
  className = '',
}) {
  const rootRef = useRef(null);
  const [open, setOpen] = useState(false);
  useEffect(() => {
    const handleOutsideClick = (event) => {
      if (
        rootRef.current &&
        !rootRef.current.contains(event.target)
      ) {
        setOpen(false);
      }
    };
    document.addEventListener(
      'mousedown',
      handleOutsideClick,
    );
    return () =>
      document.removeEventListener(
        'mousedown',
        handleOutsideClick,
      );
  }, []);
  const selectedOption =
    options.find(
      (option) =>
        String(option.value) ===
        String(value),
    ) || null;
  return (
    <div
      ref={rootRef}
      className={`menus-custom-select ${className}`}
    >
      <button
        type="button"
        className={`menus-custom-select__trigger ${
          open ? 'is-open' : ''
        }`}
        onClick={() => {
          if (!disabled) {
            setOpen(
              (current) =>
                !current,
            );
          }
        }}
        disabled={disabled}
        aria-label={ariaLabel}
        aria-expanded={open}
      >
        <span>
          {selectedOption?.label || '—'}
        </span>
        <ChevronDown
          size={16}
          className={
            open
              ? 'is-rotated'
              : ''
          }
        />
      </button>
      {open ? (
        <div
          className="menus-custom-select__menu"
          role="listbox"
        >
          {options.map(
            (option) => {
              const active =
                String(
                  option.value,
                ) ===
                String(value);
              return (
                <button
                  key={option.value}
                  type="button"
                  role="option"
                  aria-selected={active}
                  className={
                    active
                      ? 'is-active'
                      : ''
                  }
                  onClick={() => {
                    onChange?.(
                      option.value,
                    );
                    setOpen(false);
                  }}
                >
                  <span>
                    {option.label}
                  </span>
                  {active ? (
                    <Check size={15} />
                  ) : null}
                </button>
              );
            },
          )}
        </div>
      ) : null}
    </div>
  );
}
function Menus() {
  const { isFeatureAvailable } = useAuth();
  const { t, language } = useLanguage();
  const { showAlert } = useAlerts();
  const { confirm } = useConfirm();
  const direction = language === 'ar' ? 'rtl' : 'ltr';
  const establishmentId =
    localStorage.getItem(
      'selected_establishment_id',
    ) || DEFAULT_ESTABLISHMENT_ID;
  const [menus, setMenus] = useState([]);
  const [searchTerm, setSearchTerm] =
    useState('');
  const [statusFilter, setStatusFilter] =
    useState('');
  const [selectedMenuId, setSelectedMenuId] =
    useState(
      localStorage.getItem(
        SELECTED_MENU_STORAGE_KEY,
      ) || '',
    );
  const [loading, setLoading] = useState(true);
  const [actionMenuId, setActionMenuId] =
    useState('');
  const [error, setError] = useState('');
  const [establishmentSlug, setEstablishmentSlug] = useState('');
  const fetchMenus = async () => {
    try {
      setLoading(true);
      setError('');
      const response =
        await menusApi.getMenus(
          establishmentId,
          {
            per_page: 100,
            locale: language,
          },
        );
      const responseMenus = toArray(
        response.data?.data?.data ||
          response.data?.data ||
          [],
      );
      setMenus(responseMenus);
      const slugFromMenus = responseMenus.map((item) =>
        item.establishment?.slug || item.establishment_slug,
      ).find(Boolean);
      if (slugFromMenus) {
        setEstablishmentSlug(slugFromMenus);
      } else if (responseMenus[0]?.id) {
        try {
          const shareResponse = await menuTemplatesApi.getMenuShare(
            establishmentId, responseMenus[0].id,
          );
          setEstablishmentSlug(shareResponse.data?.data?.establishment_slug || '');
        } catch {
          setEstablishmentSlug('');
        }
      } else {
        setEstablishmentSlug('');
      }
      const storedMenuId =
        localStorage.getItem(
          SELECTED_MENU_STORAGE_KEY,
        ) || '';
      const storedMenuExists =
        responseMenus.some(
          (menu) =>
            String(menu.id) ===
            String(storedMenuId),
        );
      if (storedMenuExists) {
        setSelectedMenuId(storedMenuId);
        return;
      }
      const preferredMenu =
        responseMenus.find((menu) =>
          getBooleanStatus(
            menu.is_default,
          ),
        ) ||
        responseMenus.find((menu) =>
          getBooleanStatus(
            menu.is_active,
          ),
        ) ||
        responseMenus[0];
      if (preferredMenu?.id) {
        saveSelectedMenu(
          preferredMenu.id,
          establishmentId,
        );
        setSelectedMenuId(
          String(preferredMenu.id),
        );
      } else {
        saveSelectedMenu(
          null,
          establishmentId,
        );
        setSelectedMenuId('');
      }
    } catch (errorResponse) {
      const message = translateApiError(
        errorResponse,
        t,
      );
      setError(message);
      showAlert({
        type: 'error',
        title: t('alerts.error'),
        message,
      });
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => {
    fetchMenus();
  }, [establishmentId, language]);
  const filteredMenus = useMemo(() => {
    const query =
      searchTerm.trim().toLowerCase();
    return menus.filter((menu) => {
      const isActive = getBooleanStatus(
        menu.is_active,
      );
      const matchesSearch =
        !query ||
        String(menu.name || '')
          .toLowerCase()
          .includes(query) ||
        String(menu.description || '')
          .toLowerCase()
          .includes(query) ||
        String(menu.slug || '')
          .toLowerCase()
          .includes(query);
      const matchesStatus =
        !statusFilter ||
        (statusFilter === 'active'
          ? isActive
          : !isActive);
      return matchesSearch && matchesStatus;
    });
  }, [
    menus,
    searchTerm,
    statusFilter,
  ]);
  const totalMenus = menus.length;
  const activeMenus = menus.filter((menu) =>
    getBooleanStatus(menu.is_active),
  ).length;
  const inactiveMenus =
    totalMenus - activeMenus;
  const defaultMenus = menus.filter((menu) =>
    getBooleanStatus(menu.is_default),
  ).length;
  const handleSelectMenu = (menu) => {
    if (!menu?.id) return;
    saveSelectedMenu(
      menu.id,
      establishmentId,
    );
    setSelectedMenuId(String(menu.id));
  };
  const handleToggleStatus = async (menu) => {
    if (!menu?.id) return;
    const nextStatus = !getBooleanStatus(
      menu.is_active,
    );
    try {
      setActionMenuId(
        `status-${menu.id}`,
      );
      await menusApi.updateStatus(
        menu.id,
        nextStatus,
      );
      setMenus((currentMenus) =>
        currentMenus.map(
          (currentMenu) =>
            String(currentMenu.id) ===
            String(menu.id)
              ? {
                  ...currentMenu,
                  is_active: nextStatus,
                }
              : currentMenu,
        ),
      );
      showAlert({
        type: 'success',
        title: t('alerts.success'),
        message: nextStatus
          ? translateFallback(
              t,
              'menus.alerts.activated',
              'Menu activated successfully.',
            )
          : translateFallback(
              t,
              'menus.alerts.deactivated',
              'Menu deactivated successfully.',
            ),
      });
    } catch (errorResponse) {
      showAlert({
        type: 'error',
        title: t('alerts.error'),
        message: translateApiError(
          errorResponse,
          t,
        ),
      });
    } finally {
      setActionMenuId('');
    }
  };
  const handleDownloadQrCode = async (menu) => {
    if (!menu?.slug || !getBooleanStatus(menu.is_active)) {
      showAlert({
        type: 'error',
        title: t('alerts.error'),
        message: translateFallback(
          t,
          'menus.alerts.activateBeforeQr',
          'Activate this menu before downloading its QR code.',
        ),
      });
      return;
    }
    try {
      setActionMenuId(`qr-${menu.id}`);
      const publicUrl = `${window.location.origin}/menu/${menu.slug}`;
      const qrCodeDataUrl = await QRCodeGenerator.toDataURL(
        publicUrl,
        {
          width: 520,
          margin: 1,
          errorCorrectionLevel: 'H',
        },
      );
      const link = document.createElement('a');
      link.href = qrCodeDataUrl;
      link.download = `${menu.slug}-qr-code.png`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    } catch {
      showAlert({
        type: 'error',
        title: t('alerts.error'),
        message: translateFallback(
          t,
          'menus.alerts.qrDownloadFailed',
          'Unable to download the QR code.',
        ),
      });
    } finally {
      setActionMenuId('');
    }
  };
  const handleDelete = async (menu) => {
    if (!menu?.id) return;
    const confirmed = await confirm({
      variant: 'danger',
      title: translateFallback(
        t,
        'menus.confirm.title',
        'Delete this menu?',
      ),
      message: translateFallback(
        t,
        'menus.confirm.description',
        'You are about to permanently delete this menu.',
      ),
      itemName: menu.name,
      warning: translateFallback(
        t,
        'menus.confirm.warning',
        'This action cannot be undone. The public link and QR code for this menu will stop working.',
      ),
      confirmText: translateFallback(
        t,
        'menus.confirm.confirm',
        'Delete menu',
      ),
      cancelText: translateFallback(
        t,
        'common.cancel',
        'Cancel',
      ),
      closeText: translateFallback(
        t,
        'common.close',
        'Close',
      ),
    });
    if (!confirmed) return;
    try {
      setActionMenuId(
        `delete-${menu.id}`,
      );
      await menusApi.deleteMenu(menu.id);
      setMenus((currentMenus) =>
        currentMenus.filter(
          (currentMenu) =>
            String(currentMenu.id) !==
            String(menu.id),
        ),
      );
      if (
        String(selectedMenuId) ===
        String(menu.id)
      ) {
        saveSelectedMenu(
          null,
          establishmentId,
        );
        setSelectedMenuId('');
      }
      showAlert({
        type: 'success',
        title: t('alerts.success'),
        message: translateFallback(
          t,
          'menus.alerts.deleted',
          'Menu deleted successfully.',
        ),
      });
      await fetchMenus();
    } catch (errorResponse) {
      showAlert({
        type: 'error',
        title: t('alerts.error'),
        message: translateApiError(
          errorResponse,
          t,
        ),
      });
    } finally {
      setActionMenuId('');
    }
  };
  return (
    <div className="menus-page" dir={direction}>
      <section className="menus-heading">
        <div>
          <p className="menus-heading__eyebrow">
            {translateFallback(
              t,
              'menus.eyebrow',
              'Menu management',
            )}
          </p>
          <h1>
            {translateFallback(
              t,
              'menus.title',
              'Your menus',
            )}
          </h1>
          <p>
            {translateFallback(
              t,
              'menus.description',
              'Create several menus, manage their details, customize their appearance and publish only the menus that are ready.',
            )}
          </p>
        </div>
        <Link
          to="/menus/new"
          className="dashboard-primary-button"
        >
          <Plus size={18} />
          {translateFallback(
            t,
            'menus.addMenu',
            'Add menu',
          )}
        </Link>
      </section>
      <section className="menus-summary-grid">
        <article className="menus-summary-card">
          <span>
            <BookOpen size={22} />
          </span>
          <div>
            <strong>{formatNumber(totalMenus, language)}</strong>
            <p>
              {translateFallback(
                t,
                'menus.summary.total',
                'Total menus',
              )}
            </p>
          </div>
        </article>
        <article className="menus-summary-card">
          <span>
            <Power size={22} />
          </span>
          <div>
            <strong>{formatNumber(activeMenus, language)}</strong>
            <p>
              {translateFallback(
                t,
                'menus.summary.active',
                'Active menus',
              )}
            </p>
          </div>
        </article>
        <article className="menus-summary-card">
          <span>
            <PowerOff size={22} />
          </span>
          <div>
            <strong>{formatNumber(inactiveMenus, language)}</strong>
            <p>
              {translateFallback(
                t,
                'menus.summary.inactive',
                'Inactive menus',
              )}
            </p>
          </div>
        </article>
        <article className="menus-summary-card">
          <span>
            <Star size={22} />
          </span>
          <div>
            <strong>{formatNumber(defaultMenus, language)}</strong>
            <p>
              {translateFallback(
                t,
                'menus.summary.default',
                'Default menu',
              )}
            </p>
          </div>
        </article>
      </section>
      <section className="menus-toolbar">
        <label className="menus-search">
          <Search size={18} />
          <input
            id="menus-search-input"
            type="search"
            value={searchTerm}
            onChange={(event) =>
              setSearchTerm(
                event.target.value,
              )
            }
            placeholder={translateFallback(
              t,
              'menus.searchPlaceholder',
              'Search menus...',
            )}
            aria-label={translateFallback(
              t,
              'menus.searchPlaceholder',
              'Search menus...',
            )}
          />
        </label>
        <CustomSelect
          className="menus-filter"
          value={statusFilter}
          onChange={setStatusFilter}
          ariaLabel={translateFallback(
            t,
            'menus.filters.allStatuses',
            'All statuses',
          )}
          options={[
            {
              value: '',
              label: translateFallback(
                t,
                'menus.filters.all',
                'All statuses',
              ),
            },
            {
              value: 'active',
              label: translateFallback(
                t,
                'menus.filters.active',
                'Active',
              ),
            },
            {
              value: 'inactive',
              label: translateFallback(
                t,
                'menus.filters.inactive',
                'Inactive',
              ),
            },
          ]}
        />
        <button
          type="button"
          className="menus-refresh-button"
          onClick={fetchMenus}
          disabled={loading}
        >
          {loading ? (
            <Loader2 size={17} />
          ) : (
            <RefreshCcw size={17} />
          )}
          {translateFallback(
            t,
            'common.refresh',
            'Refresh',
          )}
        </button>
      </section>
      {loading ? (
        <div className="menus-state">
          <Loader2
            className="menus-spin"
            size={32}
          />
          <p>
            {translateFallback(
              t,
              'menus.loading',
              'Loading menus...',
            )}
          </p>
        </div>
      ) : error ? (
        <div className="menus-state menus-state--error">
          <p>{error}</p>
          <button
            type="button"
            onClick={fetchMenus}
          >
            {translateFallback(
              t,
              'common.retry',
              'Retry',
            )}
          </button>
        </div>
      ) : filteredMenus.length > 0 ? (
        <section className="menus-cards-grid">
          {filteredMenus.map((menu) => {
            const isActive = getBooleanStatus(
              menu.is_active,
            );
            const isDefault =
              getBooleanStatus(
                menu.is_default,
              );
            const isSelected =
              String(selectedMenuId) ===
              String(menu.id);
            const imageUrl =
              getMenuImageUrl(menu);
            const categoriesCount =
              getCount(
                menu.categories_count,
                menu.categories?.length,
              );
            const productsCount = getCount(
              menu.products_count,
              menu.products?.length,
            );
            const statusActionLoading =
              actionMenuId ===
              `status-${menu.id}`;
            const deleteActionLoading =
              actionMenuId ===
              `delete-${menu.id}`;
            return (
              <article
                key={menu.id}
                className={`menu-card ${
                  isSelected
                    ? 'menu-card--selected'
                    : ''
                }`}
              >
                <div className="menu-card__header">
                  {imageUrl ? (
                    <img
                      src={imageUrl}
                      alt={menu.name}
                    />
                  ) : (
                    <span className="menu-card__header-placeholder">
                      <UtensilsCrossed
                        size={52}
                      />
                    </span>
                  )}
                  <span
                    className={`menu-card__status-badge ${
                      isActive
                        ? 'menu-card__status-badge--active'
                        : 'menu-card__status-badge--inactive'
                    }`}
                  >
                    <span className="menu-card__status-dot" />
                    {isActive
                      ? translateFallback(
                          t,
                          'menus.status.active',
                          'Active',
                        )
                      : translateFallback(
                          t,
                          'menus.status.inactive',
                          'Inactive',
                        )}
                  </span>
                  {isDefault && (
                    <span className="menu-card__default-badge">
                      <Star
                        size={13}
                        fill="currentColor"
                      />
                      {translateFallback(
                        t,
                        'menus.status.default',
                        'Default',
                      )}
                    </span>
                  )}
                  {isSelected && (
                    <span className="menu-card__selected-badge">
                      <Check size={13} />
                      {translateFallback(
                        t,
                        'menus.status.selected',
                        'Selected',
                      )}
                    </span>
                  )}
                </div>
                <div className="menu-card__body">
                  <div className="menu-card__info">
                    <h3 className="menu-card__name">
                      {menu.name}
                    </h3>
                    <p className="menu-card__description">
                      {menu.description ||
                        translateFallback(
                          t,
                          'menus.noDescription',
                          'No description added yet.',
                        )}
                    </p>
                  </div>
                  <div className="menu-card__meta">
                    <span className="menu-card__meta-chip">
                      <Layers size={14} />
                      {formatNumber(
                        categoriesCount,
                        language,
                      )}{' '}
                      {translateFallback(
                        t,
                        'menus.categories',
                        'Categories',
                      )}
                    </span>
                    <span className="menu-card__meta-chip">
                      <ListChecks size={14} />
                      {formatNumber(
                        productsCount,
                        language,
                      )}{' '}
                      {translateFallback(
                        t,
                        'menus.products',
                        'Products',
                      )}
                    </span>
                  </div>
                  <div className="menu-card__toggle-row">
                    <span className="menu-card__toggle-label">
                      {isActive ? (
                        <Power size={16} />
                      ) : (
                        <PowerOff size={16} />
                      )}
                      {isActive
                        ? translateFallback(
                            t,
                            'menus.status.menuActive',
                            'Menu active',
                          )
                        : translateFallback(
                            t,
                            'menus.status.menuInactive',
                            'Menu inactive',
                          )}
                    </span>
                    <button
                      type="button"
                      className={`menu-toggle-switch ${
                        isActive
                          ? 'menu-toggle-switch--active'
                          : ''
                      }`}
                      onClick={() =>
                        handleToggleStatus(menu)
                      }
                      disabled={statusActionLoading}
                      aria-label={
                        isActive
                          ? translateFallback(
                              t,
                              'menus.actions.deactivate',
                              'Deactivate menu',
                            )
                          : translateFallback(
                              t,
                              'menus.actions.activate',
                              'Activate menu',
                            )
                      }
                    >
                      <span className="menu-toggle-switch__knob" />
                    </button>
                  </div>
                </div>
                <div className="menu-card__actions">
                  <div className="menu-card__primary-actions">
                    <Link
                      to={`/menus/${menu.id}`}
                      className="menu-card__action-btn menu-card__action-btn--details"
                      onClick={() =>
                        handleSelectMenu(menu)
                      }
                    >
                      <Eye size={16} />
                      {translateFallback(
                        t,
                        'menus.actions.details',
                        'View details',
                      )}
                    </Link>
                    <button
                      type="button"
                      className="menu-card__action-btn menu-card__action-btn--download"
                      onClick={() =>
                        handleDownloadQrCode(menu)
                      }
                      disabled={
                        !isActive ||
                        !menu.slug ||
                        actionMenuId ===
                          `qr-${menu.id}`
                      }
                      title={
                        isActive
                          ? translateFallback(
                              t,
                              'menus.actions.downloadQr',
                              'Download QR code',
                            )
                          : translateFallback(
                              t,
                              'menus.actions.activateBeforeQr',
                              'Activate the menu before downloading the QR code.',
                            )
                      }
                    >
                      {actionMenuId ===
                      `qr-${menu.id}` ? (
                        <Loader2 size={16} />
                      ) : (
                        <Download size={16} />
                      )}
                      {translateFallback(
                        t,
                        'menus.actions.downloadQr',
                        'Download QR',
                      )}
                    </button>
                  </div>
                  <div className="menu-card__secondary-actions">
                    <Link
                      to={`/menus/${menu.id}/edit`}
                      className="menu-card__icon-btn"
                      title={translateFallback(
                        t,
                        'menus.actions.editDetails',
                        'Edit details',
                      )}
                      onClick={() =>
                        handleSelectMenu(menu)
                      }
                    >
                      <Edit3 size={16} />
                    </Link>
                    {isFeatureAvailable('ai_menu_design') && (
                      <Link
                        to={`/menus/${menu.id}/ai-design`}
                        className="menu-card__icon-btn"
                        title={translateFallback(
                          t,
                          'menus.actions.aiDesign',
                          'Design with AI',
                        )}
                        onClick={() =>
                          handleSelectMenu(menu)
                        }
                      >
                        <Sparkles size={16} />
                      </Link>
                    )}
                    {isActive && menu.slug && (
                        <Link
                          to={(menu.establishment?.slug || menu.establishment_slug || establishmentSlug)
                            ? `/${encodeURIComponent(menu.establishment?.slug || menu.establishment_slug || establishmentSlug)}/${encodeURIComponent(menu.slug)}`
                            : '#'}
                          target="_blank"
                          rel="noreferrer"
                          className="menu-card__icon-btn"
                          title={translateFallback(
                            t,
                            'menus.actions.publicPreview',
                            'Public preview',
                          )}
                          onClick={(event) => {
                            if (!(menu.establishment?.slug || menu.establishment_slug || establishmentSlug)) {
                              event.preventDefault();
                              showAlert({ type: 'error', title: t('alerts.error'),
                                message: 'Unable to retrieve the establishment slug.' });
                              return;
                            }
                            handleSelectMenu(menu);
                          }}
                        >
                          <ExternalLink size={16} />
                        </Link>
                      )}
                    <button
                      type="button"
                      className="menu-card__icon-btn menu-card__icon-btn--danger"
                      onClick={() =>
                        handleDelete(menu)
                      }
                      disabled={
                        isDefault ||
                        deleteActionLoading
                      }
                      title={
                        isDefault
                          ? translateFallback(
                              t,
                              'menus.actions.defaultCannotDelete',
                              'The default menu cannot be deleted.',
                            )
                          : translateFallback(
                              t,
                              'menus.actions.delete',
                              'Delete',
                            )
                      }
                    >
                      {deleteActionLoading ? (
                        <Loader2 size={16} />
                      ) : (
                        <Trash2 size={16} />
                      )}
                    </button>
                  </div>
                </div>
              </article>
            );
          })}
        </section>
      ) : (
        <div className="menus-empty-state">
          <BookOpen size={42} />
          <h2>
            {translateFallback(
              t,
              'menus.empty.title',
              'No menu found',
            )}
          </h2>
          <p>
            {searchTerm || statusFilter
              ? translateFallback(
                  t,
                  'menus.empty.filteredDescription',
                  'Try changing your filters to find your menus.',
                )
              : translateFallback(
                  t,
                  'menus.empty.description',
                  'Create your first menu to start adding categories, products and QR sharing.',
                )}
          </p>
          <Link
            to="/menus/new"
            className="dashboard-primary-button"
          >
            <Plus size={18} />
            {translateFallback(
              t,
              'menus.addMenu',
              'Add menu',
            )}
          </Link>
        </div>
      )}
    </div>
  );
}
export default Menus;
